import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiCreditCard, FiCheck, FiClock, FiArrowUp, FiArrowDown, FiDollarSign, FiAlertCircle, FiLoader, FiSettings, FiExternalLink, FiFileText } from 'react-icons/fi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { PLAN_CONFIGS, getPlanById } from '@/config/planConfig';
import { supabase } from '@/integrations/supabase/client';
import CryptoPaymentModal from '@/components/billing/CryptoPaymentModal';
import InvoiceCard from '@/components/billing/InvoiceCard';
import BillingSocialProof from '@/components/billing/BillingSocialProof';
import AiQuotaWidget from '@/components/billing/AiQuotaWidget';
import { usePaymentSettings } from '@/hooks/usePaymentSettings';

interface Invoice {
  id: string;
  plan_id: string;
  amount_usd: number;
  currency: string;
  payment_method: string;
  event_source: string;
  status: string;
  created_at: string;
  invoice_number: string | null;
  billing_period_start: string | null;
  billing_period_end: string | null;
  billing_type: string | null;
}

// Return the yearly total in USD for a plan, matching the Stripe price IDs
// pinned in subscription_plans.stripe_price_id_yearly. Prefer the explicit
// priceYearly on the plan config; only fall back to a derived calc when a
// plan is missing that field (legacy rows).
const yearlyPriceFor = (plan: { priceMonthly: number | null; priceYearly?: number | null }): number => {
  if (typeof plan.priceYearly === 'number') return plan.priceYearly;
  return (plan.priceMonthly ?? 0) * 10; // legacy: 10 months = 2 free
};

// Numeric rank for upgrade/downgrade comparison (tier is a string, not orderable).
const PLAN_RANK: Record<string, number> = {
  free: 0, starter: 1, pro_trader: 2, institutional_ai: 3, enterprise: 4,
};

const AccountBilling: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { profile, loading: profileLoading, refreshProfile } = useSubscription();
  const { trialInfo, effectivePlan } = useFeatureFlags();
  
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [showCryptoModal, setShowCryptoModal] = useState(false);
  const [loadingStripe, setLoadingStripe] = useState<string | null>(null);
  const [loadingPortal, setLoadingPortal] = useState(false);
  const [checkoutMessage, setCheckoutMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const paymentConfig = usePaymentSettings();

  // Founding Member claim came from /founding — grab the coupon early so
  // downstream logic (banner, lockedToYearly bypass, discount preview)
  // can react to it before any user interaction.
  const foundingPromo = searchParams.get('founding') || undefined;

  // Annual subscribers can't switch to monthly — lock the cycle to yearly.
  // Exception 1: Founding Member flow — a user arriving via /founding is
  // picking a NEW subscription; their OLD comped/grant plan's cycle
  // should not dictate.
  // Exception 2: Only lock users who ACTUALLY have an active paid annual
  // sub. A free Starter user with billing_type='annual' as leftover state
  // (grants, comped, canceled) has nothing to preserve — they're
  // subscribing new, and locking them to yearly is a UX bug that hides
  // the monthly option they might want.
  const hasActivePaidAnnual =
    profile?.billing_type === 'annual'
    && profile?.subscription_status === 'active'
    && profile?.subscription_plan_id !== 'starter'
    && profile?.subscription_plan_id !== 'free'
    && !!profile?.subscription_plan_id;
  const lockedToYearly = hasActivePaidAnnual && !foundingPromo;
  useEffect(() => {
    if (lockedToYearly) setBillingCycle('yearly');
  }, [lockedToYearly]);

  // Handle checkout result from URL params
  useEffect(() => {
    const checkout = searchParams.get('checkout');
    if (checkout === 'success') {
      setCheckoutMessage({ type: 'success', text: 'Payment successful! Your subscription is now active.' });
      refreshProfile?.();
      setSearchParams({});
    } else if (checkout === 'canceled') {
      setCheckoutMessage({ type: 'error', text: 'Checkout was canceled. You can try again anytime.' });
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, refreshProfile]);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const { data, error } = await supabase.rpc('get_user_invoices', { p_limit: 10 });
      if (error) throw error;
      if (data?.success) {
        // All invoices returned are already paid (from billing_events with event_type = 'invoice.paid')
        setInvoices(data.invoices || []);
      }
    } catch (err) {
      console.error('Error fetching invoices:', err);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const getCurrentPlanStatus = () => {
    if (trialInfo.inTrial && trialInfo.daysRemaining !== null) {
      return {
        label: `30-Day Free Trial (Pro Trader)`,
        sublabel: `${trialInfo.daysRemaining} days remaining`,
        status: 'trialing',
        color: 'text-cyan-400'
      };
    }
    
    if (profile?.subscription_plan_id && profile?.billing_status === 'active') {
      const plan = getPlanById(profile.subscription_plan_id);
      const paidUntil = profile.paid_until ? new Date(profile.paid_until).toLocaleDateString() : 'N/A';
      return {
        label: plan?.name || profile.subscription_plan_id,
        sublabel: `Active until ${paidUntil} • ${profile.billing_provider === 'stripe' ? 'Stripe' : 'Crypto'}`,
        status: 'active',
        color: 'text-green-400'
      };
    }
    
    if (profile?.billing_status === 'past_due') {
      return {
        label: getPlanById(profile.subscription_plan_id || '')?.name || 'Past Due',
        sublabel: 'Payment failed - please update your payment method',
        status: 'past_due',
        color: 'text-orange-400'
      };
    }
    
    if (profile?.billing_status === 'expired' || profile?.billing_status === 'canceled') {
      const plan = getPlanById(profile.subscription_plan_id || '');
      return {
        label: plan?.name || 'Expired Plan',
        sublabel: 'Subscription expired - renew to continue',
        status: 'expired',
        color: 'text-red-400'
      };
    }
    
    return {
      label: 'No Active Plan',
      sublabel: 'Choose a plan to get started',
      status: 'none',
      color: 'text-muted-foreground'
    };
  };

  const getButtonAction = (planId: string) => {
    const currentPlanId = profile?.subscription_plan_id;
    const isLifetime = profile?.billing_type === 'lifetime';
    const isMonthly = profile?.billing_type === 'monthly';
    const isStripeActive = profile?.billing_provider === 'stripe' && profile?.billing_status === 'active';
    // Downgrades only make sense for a genuine active Stripe MONTHLY subscriber. Lifetime,
    // comped, crypto and annual accounts can't downgrade (it would mean paying for a worse plan).
    const canDowngrade = isStripeActive && isMonthly;

    if (trialInfo.inTrial) {
      return { label: 'Subscribe with Card', action: 'activate', icon: FiCreditCard };
    }

    if (!currentPlanId || profile?.billing_status === 'expired' || profile?.billing_status === 'none' || profile?.billing_status === 'canceled') {
      return { label: 'Subscribe with Card', action: 'activate', icon: FiCreditCard };
    }

    // Current plan tier.
    if (planId === currentPlanId) {
      // Only a real monthly Stripe subscriber can upgrade their billing to annual.
      if (canDowngrade && billingCycle === 'yearly') {
        return { label: 'Upgrade to Annual', action: 'upgrade', icon: FiArrowUp };
      }
      return { label: 'Current Plan', action: 'current', icon: FiCheck };
    }

    // Lifetime members own permanent access — never upgrade, and downgrade is disabled (comped).
    if (isLifetime) {
      return { label: 'Downgrade', action: 'disabled', icon: FiArrowDown, disabled: true };
    }

    const currentRank = PLAN_RANK[currentPlanId] ?? -1;
    const targetRank = PLAN_RANK[planId] ?? 0;

    if (targetRank > currentRank) {
      return { label: 'Upgrade', action: 'upgrade', icon: FiArrowUp };
    }

    // Lower tier = downgrade. Only actionable for a monthly Stripe subscriber; otherwise disabled.
    if (canDowngrade) {
      return { label: 'Downgrade', action: 'downgrade', icon: FiArrowDown };
    }
    return { label: 'Downgrade', action: 'disabled', icon: FiArrowDown, disabled: true };
  };

  // foundingPromo is declared once at the top of the component so both
  // lockedToYearly and handleStripeCheckout share the same value.
  const handleStripeCheckout = async (planId: string) => {
    setLoadingStripe(planId);
    setCheckoutMessage(null);

    try {
      // Fetch directly instead of supabase.functions.invoke() because the
      // SDK's invoke() opaques non-2xx bodies — you get "Edge Function
      // returned a non-2xx status code" with no way to read the real
      // Stripe error underneath. Direct fetch gives us the response body
      // regardless of status, so the user sees the actual cause
      // ("No such coupon: FOUNDING40" or similar).
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-checkout`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            plan_id: planId,
            billing_cycle: billingCycle,
            promo_code: foundingPromo,
          }),
        },
      );

      const payload = await res.json().catch(() => ({}));

      if (!res.ok) {
        // Prefer the explicit error field, fall back to a JSON dump so we
        // never end up with just "{}" or a bare status code.
        const msg =
          (payload && typeof payload.error === 'string' && payload.error)
            || (payload && Object.keys(payload).length > 0 && JSON.stringify(payload))
            || `Checkout failed with status ${res.status}. Check Supabase Function logs.`;
        throw new Error(msg);
      }

      if (payload?.url) {
        window.open(payload.url, '_blank');
      }
    } catch (err: any) {
      console.error('Checkout error:', err);
      setCheckoutMessage({ type: 'error', text: err.message || 'Failed to start checkout' });
    } finally {
      setLoadingStripe(null);
    }
  };

  const handleManageSubscription = async () => {
    setLoadingPortal(true);
    setCheckoutMessage(null);

    try {
      const { data, error } = await supabase.functions.invoke('customer-portal', {});

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (err: any) {
      console.error('Portal error:', err);
      setCheckoutMessage({ type: 'error', text: err.message || 'Failed to open billing portal' });
    } finally {
      setLoadingPortal(false);
    }
  };

  const handleCryptoPayment = (planId: string) => {
    setSelectedPlan(planId);
    setShowCryptoModal(true);
  };

  const planStatus = getCurrentPlanStatus();
  // Hide Free from the upgrade grid (monthly & yearly). Free is the silent
  // default assigned at signup (user_profiles.subscription_plan DEFAULT 'free');
  // the grid is for UPGRADES -- only paid plans should appear.
  const displayPlans = PLAN_CONFIGS.filter(p => !p.isEnterprise && p.id !== 'free');
  const hasStripeSubscription = profile?.billing_provider === 'stripe' && profile?.billing_status === 'active';

  if (profileLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 relative overflow-hidden">
      {/* Subtle grid backdrop — pure V3 terminal aesthetic */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.04] bg-[linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] bg-[size:48px_48px]" />

      <div className="relative max-w-6xl mx-auto px-4 py-10">
        {/* Header — V3 style: mono uppercase label + big black heading */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10 text-center"
        >
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-400 mb-3">
            ▸ Plan &amp; billing
          </div>
          <h1 className="text-4xl md:text-6xl font-black mb-3 text-white">
            Manage your subscription.
          </h1>
          <p className="text-base text-gray-400 font-mono">
            $ status · $ plan · $ invoices — all in one place.
          </p>
        </motion.div>

        {/* Founding Member banner — Growth Plan §2.2. Visible whenever a
            user lands here with ?founding=<coupon> so they know the -40%
            (or whatever the coupon does) will be applied on Stripe
            Checkout, not on this page's list prices. */}
        {foundingPromo && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 rounded-2xl border-2 border-cyan-400/40 bg-gradient-to-r from-cyan-500/10 via-gray-900/60 to-purple-500/10 p-5"
          >
            <div className="flex items-start gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-400 mb-1">
                  ▸ Founding member offer active
                </div>
                <div className="text-white font-bold text-lg">
                  Coupon <span className="font-mono text-cyan-300">{foundingPromo}</span> will be applied at checkout.
                </div>
                <div className="text-sm text-gray-400 mt-1">
                  Prices shown below are LIST prices — Stripe will show your discounted total on the next screen. This deal is <span className="text-white">for life</span> (never expires).
                </div>
              </div>
              <div className="text-right whitespace-nowrap">
                <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Founding discount</div>
                <div className="text-3xl font-black bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
                  40% off
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Checkout Message */}
        {checkoutMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-6 p-4 rounded-xl border font-mono text-sm ${
              checkoutMessage.type === 'success'
                ? 'bg-green-500/5 border-green-500/30 text-green-300'
                : 'bg-red-500/5 border-red-500/30 text-red-300'
            }`}
          >
            {checkoutMessage.type === 'success' ? '✓' : '✗'} {checkoutMessage.text}
          </motion.div>
        )}

        {/* Current Plan — terminal-style data readout */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="relative bg-gray-900/60 backdrop-blur-xl border border-white/10 rounded-3xl p-6 md:p-8 mb-10"
        >
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-400 mb-4">
            ▸ Current plan
          </div>

          <div className="flex items-center justify-between flex-wrap gap-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-10 gap-y-3 font-mono text-sm flex-1 min-w-0">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Plan</div>
                <div className={`text-lg font-bold ${planStatus.color}`}>{planStatus.label}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Status</div>
                <div className="flex items-center gap-2 text-white">
                  <span className={`w-2 h-2 rounded-full ${
                    planStatus.status === 'active' || planStatus.status === 'trialing' ? 'bg-green-400 animate-pulse' :
                    planStatus.status === 'past_due' ? 'bg-orange-400' :
                    planStatus.status === 'expired' ? 'bg-red-400' : 'bg-gray-500'
                  }`} />
                  <span className="uppercase text-sm">{planStatus.status || 'none'}</span>
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Detail</div>
                <div className="text-sm text-gray-300 truncate">{planStatus.sublabel}</div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {planStatus.status === 'past_due' && (
                <div className="flex items-center gap-2 px-4 py-2 bg-orange-500/10 border border-orange-500/30 rounded-xl font-mono text-xs uppercase tracking-wider">
                  <FiAlertCircle className="w-4 h-4 text-orange-400" />
                  <span className="text-orange-400 font-bold">Update Payment</span>
                </div>
              )}
              {planStatus.status === 'expired' && (
                <div className="flex items-center gap-2 px-4 py-2 bg-red-500/10 border border-red-500/30 rounded-xl font-mono text-xs uppercase tracking-wider">
                  <FiAlertCircle className="w-4 h-4 text-red-400" />
                  <span className="text-red-400 font-bold">Action Required</span>
                </div>
              )}
              {hasStripeSubscription && (
                <button
                  onClick={handleManageSubscription}
                  disabled={loadingPortal}
                  className="flex items-center gap-2 px-4 py-3 rounded-xl font-mono text-sm font-bold text-black transition-all bg-gradient-to-r from-cyan-400 to-purple-400 hover:scale-[1.02] disabled:opacity-60"
                >
                  {loadingPortal ? (
                    <FiLoader className="w-4 h-4 animate-spin" />
                  ) : (
                    <FiSettings className="w-4 h-4" />
                  )}
                  Manage Subscription
                </button>
              )}
            </div>
          </div>
        </motion.div>

        {/* Plan Cards — V3 Terminal aesthetic */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <div className="text-center mb-8">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-purple-400 mb-3">
              ▸ Choose your level
            </div>
            <h3 className="text-3xl md:text-5xl font-black text-white">
              Pick your plan.
            </h3>
          </div>

          {/* Billing Toggle — mono style */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex justify-center items-center gap-4 mb-6"
          >
            <span className={`font-mono text-xs uppercase tracking-widest transition-colors ${
              billingCycle === 'monthly' && !lockedToYearly ? 'text-white' : 'text-gray-600'
            } ${lockedToYearly ? 'line-through opacity-50' : ''}`}>
              Monthly
            </span>
            <button
              onClick={() => { if (!lockedToYearly) setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly'); }}
              disabled={lockedToYearly}
              title={lockedToYearly ? "You're on annual billing" : ''}
              className="relative inline-flex h-7 w-14 items-center rounded-full bg-gray-800 border border-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-gradient-to-r from-cyan-400 to-purple-400 transition-transform shadow-lg ${
                  billingCycle === 'yearly' ? 'translate-x-8' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={`font-mono text-xs uppercase tracking-widest transition-colors ${
              billingCycle === 'yearly' ? 'text-white' : 'text-gray-600'
            }`}>
              Annual
            </span>
            {billingCycle === 'yearly' && (
              <span className="ml-2 px-2.5 py-0.5 bg-green-500/10 border border-green-500/30 text-green-400 text-[10px] font-mono uppercase tracking-widest font-bold rounded-full">
                Save 51%
              </span>
            )}
          </motion.div>

          {/* Trust bar — mono uppercase */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15 }}
            className="flex flex-wrap justify-center items-center gap-x-6 gap-y-2 mb-10 font-mono text-[10px] uppercase tracking-widest text-gray-500"
          >
            <span className="flex items-center gap-1.5">
              <FiCheck className="w-3 h-3 text-green-400" />
              30-day money-back
            </span>
            <span className="flex items-center gap-1.5">
              <FiCheck className="w-3 h-3 text-green-400" />
              Cancel anytime
            </span>
            <span className="flex items-center gap-1.5">
              <FiCheck className="w-3 h-3 text-green-400" />
              Card &amp; crypto
            </span>
            <span className="flex items-center gap-1.5">
              <FiCheck className="w-3 h-3 text-green-400" />
              PCI-DSS · SOC 2
            </span>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {displayPlans.map((plan, index) => {
              const buttonAction = getButtonAction(plan.id);
              const isCurrentPlan = buttonAction.action === 'current';
              const isDisabledAction = buttonAction.action === 'disabled';
              const isLoading = loadingStripe === plan.id;
              const monthlyPrice = plan.priceMonthly ?? 0;
              const yearlyPrice = yearlyPriceFor(plan);
              const displayPrice = billingCycle === 'monthly' ? monthlyPrice : yearlyPrice;
              const yearlySavings = Math.max(0, monthlyPrice * 12 - yearlyPrice);
              const yearlyPercentOff = monthlyPrice > 0
                ? Math.round((yearlySavings / (monthlyPrice * 12)) * 100)
                : 0;
              const monthsFree = monthlyPrice > 0 ? Math.round(yearlySavings / monthlyPrice) : 0;
              
              // Per-plan accent — matches LandingPageV3 pricing cards.
              const isFeatured = plan.id === 'pro_trader';
              const isFlagship = plan.id === 'institutional_ai';
              const accentColor = isFlagship ? 'purple' : isFeatured ? 'cyan' : 'gray';
              const priceColorClass =
                isFlagship ? 'text-purple-400' :
                isFeatured ? 'bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent' :
                'text-white';
              const cardClass =
                isFeatured
                  ? 'border-2 border-cyan-400/50 bg-gradient-to-br from-cyan-500/10 via-gray-900/80 to-purple-500/10 shadow-2xl shadow-cyan-500/10'
                  : isFlagship
                    ? 'border border-purple-500/30 bg-gradient-to-b from-purple-900/15 to-gray-900/60'
                    : 'border border-white/10 bg-gray-900/60';
              const checkColor =
                isFlagship ? 'text-purple-400' :
                isFeatured ? 'text-cyan-400' :
                'text-gray-500';

              return (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index }}
                  className={`relative rounded-3xl ${cardClass} p-8 h-full flex flex-col ${
                    isCurrentPlan ? 'ring-2 ring-green-400/40' : ''
                  }`}
                >
                  {plan.badge && !isCurrentPlan && (
                    <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-mono uppercase tracking-[0.15em] font-bold ${
                      isFeatured
                        ? 'bg-gradient-to-r from-cyan-400 to-purple-400 text-gray-900'
                        : isFlagship
                          ? 'bg-purple-500/20 text-purple-300 border border-purple-500/40'
                          : 'bg-white/10 text-gray-300 border border-white/10'
                    }`}>
                      {plan.badge}
                    </div>
                  )}

                  {isCurrentPlan && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-green-500/20 text-green-400 border border-green-500/40 text-[10px] font-mono uppercase tracking-[0.15em] font-bold">
                      ● Current plan
                    </div>
                  )}

                  <span className={`font-mono text-[10px] uppercase tracking-[0.2em] mb-3 ${
                    isFlagship ? 'text-purple-400' : isFeatured ? 'text-cyan-400' : 'text-gray-500'
                  }`}>
                    {plan.subtitle || 'Retail'}
                  </span>
                  <h4 className="text-2xl font-bold text-white mb-4">{plan.name}</h4>

                  {/* Price display — when a Founding Member coupon is in the
                      URL (?founding=<code>), preview the discounted price
                      here so users don't think the deal was lost. We assume
                      the founding batch is 40% off (matches the FOUNDING40
                      coupon we ship); if you launch a batch with a different
                      pct, wire it in via get_active_founding_batch and pass
                      it down as prop. Real discount is still enforced by
                      Stripe on the hosted checkout page — this is a display
                      hint only. */}
                  {(() => {
                    const FOUNDING_PCT = 40; // keep in sync with FOUNDING40 coupon
                    const hasFoundingPreview = !!foundingPromo && displayPrice > 0;
                    const discounted = hasFoundingPreview
                      ? Math.round(displayPrice * (100 - FOUNDING_PCT)) / 100
                      : displayPrice;
                    return (
                      <>
                        <div className="flex items-baseline gap-2 mb-1 flex-wrap">
                          <span className={`text-5xl font-black font-mono ${priceColorClass}`}>
                            ${hasFoundingPreview ? discounted : displayPrice}
                          </span>
                          <span className="text-gray-500 text-sm">
                            /{billingCycle === 'monthly' ? 'month' : 'year'}
                          </span>
                          {hasFoundingPreview && (
                            <span className="text-gray-600 text-lg font-mono line-through">
                              ${displayPrice}
                            </span>
                          )}
                        </div>
                        {hasFoundingPreview && (
                          <p className="text-cyan-400 text-xs mb-6 font-mono">
                            FOUNDING40 · −{FOUNDING_PCT}% · locked in for life
                          </p>
                        )}
                        {!hasFoundingPreview && billingCycle === 'yearly' && monthlyPrice > 0 && (
                          <p className="text-green-400 text-xs mb-6 font-mono">
                            ${Math.round(displayPrice / 12)}/mo · save ${yearlySavings}
                            {monthsFree > 0 ? ` (${monthsFree} months free)` : ''}
                          </p>
                        )}
                        {!hasFoundingPreview && billingCycle === 'monthly' && monthlyPrice === 0 && (
                          <p className="text-cyan-400 text-xs mb-6 font-mono">
                            no card required · cancel anytime
                          </p>
                        )}
                        {!hasFoundingPreview && billingCycle === 'monthly' && monthlyPrice > 0 && (
                          <div className="mb-6" />
                        )}
                      </>
                    );
                  })()}

                  {/* AI model badge — advertises which flagship model
                      powers this tier and the fallback after the cap. */}
                  {plan.aiModelLabel && (
                    <div className={`rounded-lg border px-3 py-2 mb-4 font-mono text-[11px] leading-relaxed ${
                      isFlagship
                        ? 'border-purple-500/30 bg-purple-500/[0.06] text-purple-200'
                        : 'border-cyan-500/30 bg-cyan-500/[0.06] text-cyan-200'
                    }`}>
                      <div className="uppercase tracking-widest opacity-60 mb-0.5">
                        ▸ Powered by
                      </div>
                      <div className="text-white font-semibold">{plan.aiModelLabel}</div>
                      {plan.aiFallbackLabel && (
                        <div className="opacity-60 mt-0.5">↳ {plan.aiFallbackLabel}</div>
                      )}
                    </div>
                  )}

                  <ul className="space-y-2.5 text-sm text-gray-300 flex-grow mb-6">
                    {plan.features.slice(0, 6).map((feature, idx) => (
                      <li key={idx} className="flex gap-2">
                        <FiCheck className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                          feature.included ? checkColor : 'text-gray-700'
                        }`} />
                        <span className={feature.included ? '' : 'text-gray-600 line-through'}>
                          {feature.text}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {paymentConfig.stripeEnabled && (
                    <button
                      onClick={() => handleStripeCheckout(plan.id)}
                      disabled={isCurrentPlan || isLoading || isDisabledAction}
                      title={isDisabledAction ? "This plan can't be downgraded here — contact support to change your plan" : ''}
                      className={`inline-flex w-full justify-center items-center gap-2 px-4 py-3 rounded-xl text-sm font-bold transition-all ${
                        isCurrentPlan || isDisabledAction
                          ? 'bg-white/5 text-gray-500 cursor-not-allowed border border-white/5'
                          : isFeatured
                            ? 'bg-gradient-to-r from-cyan-400 to-purple-400 text-black hover:scale-[1.02] shadow-lg shadow-cyan-500/30'
                            : isFlagship
                              ? 'border border-purple-400/40 text-purple-300 hover:bg-purple-400/10'
                              : 'border border-white/15 text-gray-200 hover:bg-white/5'
                      }`}
                    >
                      {isLoading ? (
                        <FiLoader className="w-4 h-4 animate-spin" />
                      ) : (
                        <buttonAction.icon className="w-4 h-4" />
                      )}
                      {isLoading ? 'Processing…' : buttonAction.label}
                    </button>
                  )}

                  {/* Crypto payment — visible when the admin toggles crypto
                      ON in Payment Settings AND a valid wallet address is
                      saved (usePaymentSettings gates both). Renders as a
                      compact secondary button under the primary Stripe CTA
                      so card stays the default path and crypto is a real
                      alternative, not a competing headline. Hidden for the
                      free Starter plan (nothing to pay for) and for the
                      user's current plan / disabled downgrade slot. */}
                  {paymentConfig.cryptoEnabled
                    && !isCurrentPlan
                    && !isDisabledAction
                    && (plan.priceMonthly ?? 0) > 0 && (
                    <button
                      onClick={() => handleCryptoPayment(plan.id)}
                      className="mt-2 inline-flex w-full justify-center items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-medium border border-white/10 text-gray-300 hover:border-cyan-400/40 hover:bg-cyan-500/5 hover:text-cyan-300 transition-colors"
                    >
                      <span className="font-mono text-[10px] uppercase tracking-widest opacity-70">or</span>
                      Pay with USDT · crypto
                    </button>
                  )}
                </motion.div>
              );
            })}
          </div>

          {/* Enterprise CTA — mono style */}
          <div className="mt-10 text-center">
            <p className="text-gray-500 font-mono text-xs uppercase tracking-widest mb-2">
              Need enterprise features?
            </p>
            <button
              onClick={() => navigate('/#contact')}
              className="text-cyan-400 hover:text-cyan-300 font-mono text-sm font-bold tracking-wide transition-colors"
            >
              [ Contact us for custom pricing → ]
            </button>
          </div>
        </motion.div>

        {/* AI usage widget — spend vs plan cap + request Claude access + top-up.
            Placed above social proof so heavy users see their quota before
            considering an upgrade. */}
        <div className="mb-6">
          <AiQuotaWidget />
        </div>

        {/* Social proof + value anchor + testimonial — sits right where a
            user hesitates between plan grid and clicking 'Manage'. */}
        <BillingSocialProof />

        {/* Billing History — V3 terminal aesthetic */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="relative bg-gray-900/60 backdrop-blur-xl border border-white/10 rounded-3xl p-6 md:p-8"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-400 mb-2">
                ▸ Billing history
              </div>
              <h3 className="text-xl md:text-2xl font-black text-white">Invoices &amp; receipts</h3>
            </div>
            {invoices.length > 0 && (
              <span className="font-mono text-xs uppercase tracking-widest text-gray-500">
                {invoices.length} entr{invoices.length > 1 ? 'ies' : 'y'}
              </span>
            )}
          </div>

          {loadingInvoices ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-cyan-400 mx-auto mb-3"></div>
              <p className="font-mono text-xs uppercase tracking-widest text-gray-500">Loading invoices…</p>
            </div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-14 border border-dashed border-white/10 rounded-2xl">
              <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-black/40 border border-white/10 flex items-center justify-center">
                <FiFileText className="w-6 h-6 text-gray-600" />
              </div>
              <p className="text-white font-medium mb-1">No invoices yet</p>
              <p className="font-mono text-[11px] uppercase tracking-widest text-gray-500">
                First payment will appear here
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {invoices.map((invoice) => (
                <InvoiceCard 
                  key={invoice.id}
                  invoice={invoice}
                  userEmail={profile?.email || ''}
                  userName={profile?.name || profile?.first_name || ''}
                />
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {/* Crypto Payment Modal */}
      {showCryptoModal && selectedPlan && (
        <CryptoPaymentModal
          planId={selectedPlan}
          billingCycle={billingCycle}
          // Threads the Founding Member promo through so the crypto
          // modal shows the same discounted price the user sees on
          // the plan card + the same one Stripe applies at checkout.
          foundingPromo={foundingPromo}
          onClose={() => {
            setShowCryptoModal(false);
            setSelectedPlan(null);
          }}
          onSuccess={() => {
            setShowCryptoModal(false);
            setSelectedPlan(null);
            fetchInvoices();
          }}
        />
      )}
    </div>
  );
};

export default AccountBilling;
